import { useEffect, useState } from "react";
import type { ApiError, ObservationsResponse } from "@/types";
import * as api from "@/lib/api";

interface UseObservationsResult {
  data: ObservationsResponse | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => void;
}

export function useObservations(seriesId: string | undefined, from: string, to: string): UseObservationsResult {
  const [data, setData] = useState<ObservationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!seriesId || !from || !to) return;

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .getObservations(seriesId, from, to, controller.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof Error && err.name === "AbortError") return;
        setError(api.isApiError(err) ? err : { error: err instanceof Error ? err.message : "Unknown error" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [seriesId, from, to, attempt]);

  return { data, loading, error, refetch: () => setAttempt((a) => a + 1) };
}
